var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { ButtonList } from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import { memo, useContext, useEffect, useMemo, useState, } from 'react';
import { useMount } from 'react-use';
import { EmailRenderer } from 'components/emailRenderer';
import { TranslateTextDropdownButton } from 'components/translateTextDropdownButton';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import RecipientPill from '../../containers/messages/recipientPill';
import { ConversationContext } from '../ConversationProvider';
import { MessageIframe } from '../messageIframe';
import TextMessage from './TextMessage';
import { DetailedErrorMessage } from './detailedErrorMessage';
import { useMessageFiles } from './utils/useMessageFiles';
function escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
function highlightMessage(message, issue, intent) {
    let highlightedContent = message;
    const issueRegex = new RegExp(escapeRegExp(issue), 'i');
    const intentRegex = new RegExp(escapeRegExp(intent), 'i');
    if (issue) {
        highlightedContent = highlightedContent === null || highlightedContent === void 0 ? void 0 : highlightedContent.replace(issueRegex, `<span style="background-color: ${colors.purple[300]};
      ">${issue}</span>`);
    }
    if (intent) {
        highlightedContent = highlightedContent === null || highlightedContent === void 0 ? void 0 : highlightedContent.replace(intentRegex, `<span style="background-color: ${colors.orange[200]}">${intent}</span>`);
    }
    return highlightedContent;
}
const MessageEmailHTML = (props) => {
    const { keyIssueExtraction, emailTranslation, shadowDomMessages } = useFeatureFlag([
        'key-issue-extraction',
        'email-translation',
        'shadow-dom-messages',
    ]);
    const [content, setContent] = useState(null);
    const [contentIsPlainText, setContentIsPlainText] = useState(false);
    const [renderedContent, setRenderedContent] = useState(content);
    const [isTranslated, setIsTranslated] = useState(false);
    const { translationLanguageCode } = useContext(ConversationContext);
    const [selectedLanguageCode, setSelectedLanguageCode] = useState();
    useEffect(() => {
        setSelectedLanguageCode(translationLanguageCode);
    }, [translationLanguageCode]);
    useMount(() => {
        const urlArray = props.orignalUrl.split('/');
        const messageId = urlArray.pop();
        const foundMessage = props.htmlMessages.find((m) => m.messageId === messageId);
        if (foundMessage) {
            setContent(foundMessage.rewritten_html);
        }
        else {
            props
                .originalEmailGetHtmlMessage({
                url: props.orignalUrl,
                csid: props.csid,
            })
                .then((r) => {
                var _a, _b;
                setContent(((_a = r.msg) === null || _a === void 0 ? void 0 : _a.rewritten_html) || r.rewritten_html || null);
                // This will be used when we will render text stored in S3.
                setContentIsPlainText(!((_b = r.msg) === null || _b === void 0 ? void 0 : _b.rewritten_html) && !r.rewritten_html);
            })
                .catch((e) => {
                console.error(e);
            });
        }
    });
    useEffect(() => {
        if (keyIssueExtraction) {
            setContent((content) => content && content.length > 0
                ? highlightMessage(content, props.keyIssue, props.messageIntent)
                : null);
        }
    }, [
        props.htmlMessages,
        props.keyIssue,
        props.messageIntent,
        keyIssueExtraction,
    ]);
    useEffect(() => setRenderedContent(content), [content]);
    const prepareRecipientMenu = ({ cc_list_v2, bcc_list_v2, to_list_v2, to, from, }) => {
        const toArray = to ? [to] : [];
        const toList = Array.isArray(to_list_v2) ? to_list_v2 : toArray;
        const ccList = Array.isArray(cc_list_v2) ? cc_list_v2 : [];
        const bccList = Array.isArray(bcc_list_v2) ? bcc_list_v2 : [];
        if (toList.length || ccList.length || bccList.length) {
            const fromAttributeAsRecipient = {
                email: from ? from : null,
                name: null,
            };
            return (_jsx(RecipientPill, { id: `cc-bcc-pill-${props.message.id}`, from: fromAttributeAsRecipient, toList: toList, ccList: ccList, bccList: bccList }));
        }
        return null;
    };
    const { message, onShowOriginal, onFetchPrinter, originalEmailGetHtmlMessage, originalEmailMeta, orignalUrl, htmlMessages, keyIssue, messageIntent } = props, otherProps = __rest(props, ["message", "onShowOriginal", "onFetchPrinter", "originalEmailGetHtmlMessage", "originalEmailMeta", "orignalUrl", "htmlMessages", "keyIssue", "messageIntent"]);
    const menu = [
        ...(typeof otherProps.failed === 'string'
            ? [
                _jsx(_Fragment, { children: _jsx(DetailedErrorMessage, { message: otherProps.failed, description: otherProps.failureReason }) }),
            ]
            : []),
        ...(onShowOriginal
            ? [
                _jsxs(_Fragment, { children: [_jsx(ButtonList.Item, Object.assign({ icon: "mail", isLoading: originalEmailMeta.asyncStatus === 'loading', onClick: onShowOriginal }, { children: "View original email" })), _jsx(ButtonList.Item, Object.assign({ icon: "print", isLoading: originalEmailMeta.printerFriendly === 'loading', onClick: onFetchPrinter }, { children: "Print email" }))] }),
            ]
            : []),
    ];
    const messageContent = useMemo(() => {
        if (renderedContent) {
            const messageProps = {
                content: renderedContent,
                isPlainText: contentIsPlainText,
            };
            return shadowDomMessages ? (_jsx(EmailRenderer, { html: renderedContent })) : (_jsx(MessageIframe, Object.assign({}, messageProps)));
        }
    }, [renderedContent, contentIsPlainText, shadowDomMessages]);
    const { attachments, images } = useMessageFiles(message.inline_images, message.attached_files);
    const gutterButtons = useMemo(() => {
        if (!emailTranslation || !content) {
            return undefined;
        }
        return (_jsx(TranslateTextDropdownButton, { message: content, selectedLanguageCode: selectedLanguageCode, onTranslateEnd: (event) => {
                setRenderedContent(event.translatedText);
                setIsTranslated(true);
            }, onTranslateError: () => {
                setIsTranslated(false);
            }, onRevert: () => {
                setRenderedContent(content);
                setIsTranslated(false);
            } }));
    }, [emailTranslation, content, selectedLanguageCode]);
    return (_jsx(TextMessage, Object.assign({ attachments: attachments, images: images, nameMenu: prepareRecipientMenu(message), menu: menu, content: messageContent, message: message, gutterButtons: gutterButtons, isTranslated: isTranslated }, otherProps)));
};
export default memo(MessageEmailHTML);
